import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect, useState } from 'react';
import { getLatestSWChangeListForVerification, getSWImportChangeListById, getCombinedSWChangeListsForImport } from '../../services/apiService';
import { decodedToken } from '../../utils/auth';

const SWAssessmentContext = createContext();

const SW_STORAGE_KEY = 'sw_assessment_context_state';
const SW_ASSESSMENT_ITEMS = ['SW'];

const createEmptyTab = () => ({
  completedItems: new Set(),
  selectedChanges: { SW: [] },
  referenceModels: { SW: '' },
  changeListId: null,
  changeListData: null,

  // NEW FIELDS
  lastModified: null,
  uploadDate: null,

  dataLoaded: false,
  loading: false,
  dataLoadAttempted: false,
  dataSource: null
});

const initialState = {
  tabs: {
    TV: createEmptyTab(),
    MNT: createEmptyTab(),
    PD: createEmptyTab(),
    AVA: createEmptyTab()
  },
  currentTab: 'TV',
  currentUserId: null
};
 const loadStateFromStorage = () => {

   try {
     const serialized = localStorage.getItem(SW_STORAGE_KEY);
     if (!serialized) return initialState;
     const parsed = JSON.parse(serialized);

    const cleanedTabs = {};
    const savedTabKeys = Object.keys(parsed?.tabs || {});
    const allTabKeys = new Set([
      ...Object.keys(initialState.tabs),
      ...savedTabKeys
    ]);

    allTabKeys.forEach(tab => {
      cleanedTabs[tab] = {
        ...createEmptyTab(),
       completedItems: new Set(
          parsed?.tabs?.[tab]?.completedItems || []
        ),
        selectedChanges:
          parsed?.tabs?.[tab]?.selectedChanges || { SW: [] },
        referenceModels:
          parsed?.tabs?.[tab]?.referenceModels || { SW: '' }
      };
    });

     return {
       ...initialState,
       currentTab: parsed.currentTab || 'TV',
       currentUserId: parsed.currentUserId || null,
       tabs: cleanedTabs
     };

   } catch (err) {
     console.error('Error loading SW state from localStorage:', err);
     return initialState;
   }
 };

const saveStateToStorage = (state) => {
  try {
    const serializable = {
      ...state,
      tabs: Object.keys(state.tabs).reduce((acc, tab) => {
        acc[tab] = {
          completedItems: Array.from(state.tabs[tab].completedItems),
          selectedChanges: state.tabs[tab].selectedChanges,
          referenceModels: state.tabs[tab].referenceModels
        };
        return acc;
      }, {})
    };
    localStorage.setItem(SW_STORAGE_KEY, JSON.stringify(serializable));
  } catch (err) {
    console.error('Error saving SW state to localStorage:', err);
  }
};

const ACTIONS = {
  SET_CURRENT_TAB: 'SET_CURRENT_TAB',
  SET_USER_ID: 'SET_USER_ID',
  SET_CHANGE_LIST: 'SET_CHANGE_LIST',
  SET_LOADING: 'SET_LOADING',
  SET_DATA_LOAD_ATTEMPTED: 'SET_DATA_LOAD_ATTEMPTED',
  SET_SELECTED_CHANGES: 'SET_SELECTED_CHANGES',
  SET_REFERENCE_MODEL: 'SET_REFERENCE_MODEL',
  SET_COMPLETED_ITEM: 'SET_COMPLETED_ITEM',
  REMOVE_COMPLETED_ITEM: 'REMOVE_COMPLETED_ITEM',
  TOGGLE_CHANGE_SELECTION: 'TOGGLE_CHANGE_SELECTION',
  UPDATE_TAB_DATA: 'UPDATE_TAB_DATA',
  CLEAR_TAB_DATA: 'CLEAR_TAB_DATA',
  CLEAR_ALL_DATA: 'CLEAR_ALL_DATA',
  LOAD_SAVED_ASSESSMENT: 'LOAD_SAVED_ASSESSMENT',
  SET_DATA_SOURCE: 'SET_DATA_SOURCE',
  INVALIDATE_SW_UPLOAD_DATA: 'INVALIDATE_SW_UPLOAD_DATA',
};

const isRowApplied = (row) => {
  const value = row?.applied ?? row?.changeAppliedNorm ?? row?.changeApplied ?? '';
  return String(value).trim().toUpperCase() === 'YES';
};

const getTabData = (state, tab) => {
  return state.tabs[tab] || createEmptyTab();
};

const swAssessmentReducer = (state, action) => {
  switch (action.type) {

    case ACTIONS.SET_CURRENT_TAB:
      return {
        ...state,
        currentTab: action.payload
      };

    case ACTIONS.SET_USER_ID:
      return {
        ...state,
        currentUserId: action.payload
      };

    case ACTIONS.SET_LOADING: {
      const tabData = getTabData(
        state,
        action.payload.tab
      );

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...tabData,
            loading: action.payload.loading
          }
        }
      };
    }

    case ACTIONS.SET_DATA_LOAD_ATTEMPTED: {
      const tabData = getTabData(
        state,
        action.payload.tab
      );

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...tabData,
            dataLoadAttempted:
              action.payload.attempted
          }
        }
      };
    }

    case ACTIONS.SET_DATA_SOURCE: {
      const tabData = getTabData(
        state,
        action.payload.tab
      );

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...tabData,
            dataSource: action.payload.source
          }
        }
      };
    }

    case ACTIONS.SET_CHANGE_LIST: {
      const tabData = getTabData(
        state,
        action.payload.tab
      );

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...tabData,
            changeListId:
              action.payload.changeListId,
            changeListData:
              action.payload.changeListData,
            lastModified:
              action.payload.lastModified,
            uploadDate:
              action.payload.uploadDate,
            dataLoaded:
              action.payload.changeListData !== null,
            loading: false,
            dataLoadAttempted: true,
            dataSource:
              action.payload.dataSource || 'imported',
            selectedChanges:
              action.payload.preSelectedChanges
                ? action.payload.preSelectedChanges
                : tabData.selectedChanges
          }
        }
      };
    }

    case ACTIONS.LOAD_SAVED_ASSESSMENT: {
      const tabData = getTabData(
        state,
        action.payload.tab
      );

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...tabData,
            ...action.payload.data,
            loading: false,
            dataLoadAttempted: true,
            dataSource: 'saved',
            completedItems:
              action.payload.data?.completedItems
                ? new Set(
                    action.payload.data.completedItems
                  )
                : tabData.completedItems
          }
        }
      };
    }

    case ACTIONS.SET_SELECTED_CHANGES: {
      const tabData = getTabData(
        state,
        action.payload.tab
      );

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...tabData,
            selectedChanges: {
              ...(tabData.selectedChanges || {}),
              ...action.payload.selectedChanges
            }
          }
        }
      };
    }

    case ACTIONS.SET_REFERENCE_MODEL: {
      const tabData = getTabData(
        state,
        action.payload.tab
      );

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...tabData,
            referenceModels: {
              ...(tabData.referenceModels || {}),
              [action.payload.item]:
                action.payload.value
            }
          }
        }
      };
    }

    case ACTIONS.SET_COMPLETED_ITEM: {
      const tabData = getTabData(
        state,
        action.payload.tab
      );

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...tabData,
            completedItems: new Set([
              ...(tabData.completedItems || []),
              action.payload.item
            ])
          }
        }
      };
    }

    case ACTIONS.REMOVE_COMPLETED_ITEM: {
      const tabData = getTabData(
        state,
        action.payload.tab
      );

      const updated = new Set(
        tabData.completedItems || []
      );

      updated.delete(action.payload.item);

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...tabData,
            completedItems: updated
          }
        }
      };
    }

    case ACTIONS.TOGGLE_CHANGE_SELECTION: {
      const {
        tab,
        item,
        changeId,
        changeDetail,
        isSelected
      } = action.payload;

      const tabData = getTabData(
        state,
        tab
      );

      const current =
        tabData.selectedChanges?.[item] || [];

      let updatedChanges;

      if (isSelected) {
        const idx = current.findIndex(
          c => c.id === changeId
        );

        if (idx === -1) {
          updatedChanges = [
            ...current,
            {
              id: changeId,
              detail: changeDetail,
              applied: true
            }
          ];
        } else {
          updatedChanges = current.map(
            (c, i) =>
              i === idx
                ? {
                    ...c,
                    applied: true
                  }
                : c
          );
        }
      } else {
        updatedChanges = current.filter(
          c => c.id !== changeId
        );
      }

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [tab]: {
            ...tabData,
            selectedChanges: {
              ...(tabData.selectedChanges || {}),
              [item]: updatedChanges
            }
          }
        }
      };
    }

    case ACTIONS.UPDATE_TAB_DATA: {
      const tabData = getTabData(
        state,
        action.payload.tab
      );

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...tabData,
            ...action.payload.data
          }
        }
      };
    }

    case ACTIONS.CLEAR_TAB_DATA:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: createEmptyTab()
        }
      };
      case ACTIONS.INVALIDATE_SW_UPLOAD_DATA: {
  const tab = action.payload.tab;

  console.log(
    'Invalidating deleted SW upload data:',
    tab
  );

  return {
    ...state,
    tabs: {
      ...state.tabs,

      [tab]: createEmptyTab()
    }
  };
}

    case ACTIONS.CLEAR_ALL_DATA:
      return {
        ...initialState,
        currentTab: state.currentTab,
        currentUserId: state.currentUserId
      };

    default:
      return state;
  }
};

// ── Provider ──────────────────────────────────────────────────────────────────
export const SWAssessmentProvider = ({ children }) => {
  const [state, dispatch] = useReducer(
    swAssessmentReducer,
    initialState,
    loadStateFromStorage
  );
  const loadingTimeouts = useRef(new Map());

  useEffect(() => {
    saveStateToStorage(state);
  }, [state]);

  // ── Action creators ─────────────────────────────────────────────────────────
  const setCurrentTab = useCallback((tab) => dispatch({ type: ACTIONS.SET_CURRENT_TAB, payload: tab }), []);
  const setUserId = useCallback((id) => dispatch({ type: ACTIONS.SET_USER_ID, payload: id }), []);
  const setLoading = useCallback((tab, loading) => dispatch({ type: ACTIONS.SET_LOADING, payload: { tab, loading } }), []);
  const setDataLoadAttempted = useCallback((tab, attempted) => dispatch({ type: ACTIONS.SET_DATA_LOAD_ATTEMPTED, payload: { tab, attempted } }), []);
  const setDataSource = useCallback((tab, source) => dispatch({ type: ACTIONS.SET_DATA_SOURCE, payload: { tab, source } }), []);
  const setChangeList = useCallback((
  tab,
  changeListId,
  changeListData,
  preSelectedChanges = null,
  dataSource = 'imported',
  lastModified = null,
  uploadDate = null
) => {
  dispatch({
     type: ACTIONS.SET_CHANGE_LIST,
  payload: {
    tab,
    changeListId,
    changeListData,
    preSelectedChanges,
    dataSource,
    lastModified,
    uploadDate
  }
  });
}, []);

  const setSelectedChanges = useCallback((tab, selectedChanges) => dispatch({ type: ACTIONS.SET_SELECTED_CHANGES, payload: { tab, selectedChanges } }), []);
  const setReferenceModel = useCallback((tab, item, value) => dispatch({ type: ACTIONS.SET_REFERENCE_MODEL, payload: { tab, item, value } }), []);
  const setCompletedItem = useCallback((tab, item) => dispatch({ type: ACTIONS.SET_COMPLETED_ITEM, payload: { tab, item } }), []);
  const removeCompletedItem = useCallback((tab, item) => dispatch({ type: ACTIONS.REMOVE_COMPLETED_ITEM, payload: { tab, item } }), []);

  const toggleChangeSelection = useCallback((tab, item, changeId, changeDetail, isSelected) => {
    dispatch({ type: ACTIONS.TOGGLE_CHANGE_SELECTION, payload: { tab, item, changeId, changeDetail, isSelected } });
  }, []);

  const updateTabData = useCallback((tab, data) => dispatch({ type: ACTIONS.UPDATE_TAB_DATA, payload: { tab, data } }), []);

  const clearTabData = useCallback((tab) => {
    if (loadingTimeouts.current.has(tab)) {
      clearTimeout(loadingTimeouts.current.get(tab));
      loadingTimeouts.current.delete(tab);
    }
    dispatch({ type: ACTIONS.CLEAR_TAB_DATA, payload: { tab } });
  }, []);

  const invalidateSWUploadData = useCallback(
  (tab) => {
    dispatch({
      type: ACTIONS.INVALIDATE_SW_UPLOAD_DATA,
      payload: { tab }
    });
  },
  []
);

  const clearAllData = useCallback(() => {
    loadingTimeouts.current.forEach(t => clearTimeout(t));
    loadingTimeouts.current.clear();
    dispatch({ type: ACTIONS.CLEAR_ALL_DATA });
  }, []);

const loadSWChangeListData = useCallback(
  async (changeListId, tab) => {
    console.log(
      '========== SW LOAD DEBUG =========='
    );
    console.log({
      changeListId,
      tab
    });

    try {
      console.log(
        `Loading SW change list data for ${tab}...`
      );

      setLoading(tab, true);

      const decoded = decodedToken();
      const userEmail = decoded?.email;
      const userType = decoded?.userType || 'Admin';

      if (!userEmail || !userType) {
        throw new Error(
          'User email and type not found. Please log in again.'
        );
      }
      console.log(
        'SW user information:',
        {
          userEmail,
          userType,
          productType: tab
        }
      );
      console.log(
        'Fetching COMBINED SW change list...'
      );
      const result =
        await getCombinedSWChangeListsForImport(
          tab,
          userEmail,
          userType
        );
      console.log(
        '========== COMBINED SW API RESULT =========='
      );

      console.log(
        'Combined API result:',
        result
      );
      if (!result?.success) {
        throw new Error(
          result?.message ||
          result?.error ||
          'No combined SW change list available'
        );
      }
      const combinedRows =
        Array.isArray(result.combinedChangeList)
          ? result.combinedChangeList
          : [];

      console.log(
        'Combined SW items:',
        combinedRows
      );

      console.log(
        'Combined SW item count:',
        combinedRows.length
      );

      const uploads =
        Array.isArray(result.userUploads)
          ? result.userUploads
          : [];

      console.log(
        'All SW uploads:',
        uploads
      );

      console.log(
        'Number of SW uploads:',
        uploads.length
      );

      uploads.forEach(
        (upload, index) => {
          console.log(
            `SW Upload ${index + 1}:`,
            {
              id: upload.id,
              filename: upload.filename,
              version: upload.version,
              uploadDate: upload.uploadDate
            }
          );
        }
      );

      const data = combinedRows;
      const latestUpload =
        uploads.length > 0
          ? uploads[uploads.length - 1]
          : null;
      const metadata = {
        id: 'combined',
        filename:
          `Combined SW Change List (${uploads.length} files)`,
        productType: tab,
        source: 'sw-import-combined',
        uploadDate:
          latestUpload?.uploadDate || null,
        lastModified:
          latestUpload?.uploadDate || null,
        uploads
      };

      console.log(
        'Combined metadata:',
        metadata
      );
      const preSelectedChanges = {};
      const swRows =
        Array.isArray(data)
          ? data
          : [];

      const preSelected =
        swRows
          .filter(isRowApplied)
          .map(row => ({
            id: row.id,
            detail: row.detail,
            applied: true
          }));

      console.log(
        'Pre-selected combined SW items:',
        preSelected
      );

      console.log(
        'Pre-selected combined SW count:',
        preSelected.length
      );

      if (preSelected.length > 0) {
        preSelectedChanges.SW =
          preSelected;
      }

      const normalizedData = {
        SW: data
      };

      console.log(
        'Normalized combined SW data:',
        normalizedData
      );

      setChangeList(
        tab,
        'combined',
        normalizedData,
        preSelectedChanges,
        metadata.source,
        metadata.lastModified,
        metadata.uploadDate
      );
      console.log(
        '=========================================='
      );
      console.log(
        '✓ Successfully loaded COMBINED SW change list'
      );
      console.log(
        'Total combined items:',
        data.length
      );
      console.log(
        'Combined item IDs:',
        data.map(row => row.id)
      );
      console.log(
        '=========================================='
      );
      return normalizedData;
    } catch (error) {
      console.error(
        `Failed to load combined SW change list for ${tab}:`,
        error
      );
      setLoading(
        tab,
        false
      );
      setDataLoadAttempted(
        tab,
        true
      );
      throw error;
    }
  },
  [
    setLoading,
    setChangeList,
    setDataLoadAttempted
  ]
);

  const getCurrentTabData = useCallback(() => {
    return state.tabs[state.currentTab] ?? createEmptyTab();
  }, [state.tabs, state.currentTab]);

  const getTabData = useCallback((tab) => {
    return state.tabs[tab] ?? createEmptyTab();
  }, [state.tabs]);

  const isAllCompleted = useCallback((tab) => {
    const tabData = state.tabs[tab];
    if (!tabData) return false;
    return SW_ASSESSMENT_ITEMS.every(item => tabData.completedItems.has(item));
  }, [state.tabs]);

  const hasChangeList = useCallback((tab) => {
    return state.tabs[tab]?.changeListId !== null ?? false;
  }, [state.tabs]);

  const isDataLoaded = useCallback((tab) => {
    return state.tabs[tab]?.dataLoaded ?? false;
  }, [state.tabs]);

  const isLoading = useCallback((tab) => {
    return state.tabs[tab]?.loading ?? false;
  }, [state.tabs]);

  const hasDataLoadAttempted = useCallback((tab) => {
    return state.tabs[tab]?.dataLoadAttempted ?? false;
  }, [state.tabs]);

  const getDataSource = useCallback((tab) => {
    return state.tabs[tab]?.dataSource ?? null;
  }, [state.tabs]);

  const value = {
    ...state,
    SW_ASSESSMENT_ITEMS,
    setCurrentTab,
    setUserId,
    setLoading,
    setDataLoadAttempted,
    setDataSource,
    setChangeList,
    setSelectedChanges,
    setReferenceModel,
    setCompletedItem,
    removeCompletedItem,
    toggleChangeSelection,
    updateTabData,
    clearTabData,
    invalidateSWUploadData,
    clearAllData,
    loadSWChangeListData,
    getCurrentTabData,
    getTabData,
    isAllCompleted,
    hasChangeList,
    isDataLoaded,
    isLoading,
    hasDataLoadAttempted,
    getDataSource
  };

  return (
    <SWAssessmentContext.Provider value={value}>
      {children}
    </SWAssessmentContext.Provider>
  );
};

export const useSWAssessment = () => {
  const context = useContext(SWAssessmentContext);
  if (!context) {
    throw new Error('useSWAssessment must be used within a SWAssessmentProvider');
  }
  return context;
};

export default SWAssessmentContext;

